import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import {
  getQuickPassProfileByUserId,
  createOrUpdateQuickPassProfile,
  saveListing as saveListingRequest,
  unsaveListing as unsaveListingRequest,
} from '../_data/quickpass-profile'
import { QuickPassProfile } from '../_types/quickpass-profile'
import { useAuth } from './use-auth'
import {
  QuickPassProfileContextValue,
  QuickPassProfileProviderProps,
} from './quickpass-profile-context-types'
import { QuickPassProfileContext } from './use-quickpass-profile'

/**
 * Loads the current renter's profile after auth resolves and exposes
 * profile actions (patch, save listing, unsave listing) via Context. The
 * profile is keyed by `quickPassUser.id` and lazily resolved — when no
 * QuickPass user is signed in, profile is null and `isProfileLoading` is
 * false. Sits below AuthProvider in the App tree.
 *
 * @param {QuickPassProfileProviderProps} props
 * @returns {JSX.Element}
 */
const QuickPassProfileProvider = ({
  children,
}: QuickPassProfileProviderProps): JSX.Element => {
  const { quickPassUser, isLoading: isAuthLoading } = useAuth()
  const [profile, setProfile] = useState<QuickPassProfile | null>(null)
  const [isProfileLoading, setIsProfileLoading] = useState<boolean>(true)
  const lastResolvedUserIdReference = useRef<string | null>(null)

  const fetchProfile = useCallback(
    async (quickPassUserId: string): Promise<void> => {
      setIsProfileLoading(true)

      try {
        const resolvedProfile =
          await getQuickPassProfileByUserId(quickPassUserId)
        setProfile(resolvedProfile)
      } catch (error) {
        console.error('Failed to fetch QuickPass profile', error)
        setProfile(null)
      } finally {
        setIsProfileLoading(false)
      }
    },
    [],
  )

  useEffect(() => {
    if (isAuthLoading) {
      return
    }

    if (quickPassUser === null) {
      setProfile(null)
      setIsProfileLoading(false)
      lastResolvedUserIdReference.current = null
      return
    }

    if (lastResolvedUserIdReference.current === quickPassUser.id) {
      return
    }

    lastResolvedUserIdReference.current = quickPassUser.id
    void fetchProfile(quickPassUser.id)
  }, [quickPassUser, isAuthLoading, fetchProfile])

  const refreshProfile = useCallback(async (): Promise<void> => {
    if (quickPassUser === null) {
      return
    }

    await fetchProfile(quickPassUser.id)
  }, [quickPassUser, fetchProfile])

  const patchProfile = useCallback(
    async (patch: Partial<QuickPassProfile>): Promise<void> => {
      if (quickPassUser === null) {
        throw new Error(
          'Cannot patch profile while signed out — caller must wait for auth.',
        )
      }

      const updated = await createOrUpdateQuickPassProfile({
        quickPassUserId: quickPassUser.id,
        patch,
      })

      setProfile(updated)
    },
    [quickPassUser],
  )

  const saveListingToProfile = useCallback(
    async (listingId: string): Promise<void> => {
      if (quickPassUser === null) {
        throw new Error('Cannot save listing while signed out.')
      }

      const updated = await saveListingRequest(quickPassUser.id, listingId)

      setProfile(updated)
    },
    [quickPassUser],
  )

  const unsaveListingFromProfile = useCallback(
    async (listingId: string): Promise<void> => {
      if (quickPassUser === null) {
        throw new Error('Cannot unsave listing while signed out.')
      }

      const updated = await unsaveListingRequest(quickPassUser.id, listingId)

      setProfile(updated)
    },
    [quickPassUser],
  )

  const hasFinishedOnboarding =
    profile !== null && profile.onboardingCompletedAt !== null

  const value = useMemo<QuickPassProfileContextValue>(
    () => ({
      profile,
      isProfileLoading,
      hasFinishedOnboarding,
      refreshProfile,
      patchProfile,
      saveListingToProfile,
      unsaveListingFromProfile,
    }),
    [
      profile,
      isProfileLoading,
      hasFinishedOnboarding,
      refreshProfile,
      patchProfile,
      saveListingToProfile,
      unsaveListingFromProfile,
    ],
  )

  return (
    <QuickPassProfileContext.Provider value={value}>
      {children}
    </QuickPassProfileContext.Provider>
  )
}

export { QuickPassProfileProvider }
